import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import "./StudentComprehensiveReportModal.css";
const StudentComprehensiveReportModal = ({
  open,
  onClose,
  student,
  reports = [],
  loading = false,
  onGenerateReport,
  onDeleteReport,
}) => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportDetails, setShowReportDetails] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setShowReportDetails(true);
  };

  const handleBackToList = () => {
    setShowReportDetails(false);
    setSelectedReport(null);
  };

  const handleGenerateNewReport = async () => {
    if (onGenerateReport) {
      await onGenerateReport();
    }
  };

  const handleClose = () => {
    setShowReportDetails(false);
    setSelectedReport(null);
    onClose();
  };

  const handleCopyReport = async () => {
    if (!selectedReport) return;

    const reportText = `
STUDENT COMPREHENSIVE REPORT
============================

Student: ${student.name || student.username || "N/A"}
Student ID: ${student?.id}
Generated Date: ${new Date(selectedReport.created_at).toLocaleDateString()}

SUMMARY
-------
Total Sessions: ${selectedReport?.total_sessions}
Total Quizzes: ${selectedReport?.total_quizzes}
Overall Performance: ${selectedReport.overall_performance}%

STRENGTH POINTS
---------------
${selectedReport.strength_points || "N/A"}

POINTS TO IMPROVE
-----------------
${selectedReport.points_to_improve || "N/A"}

RECOMMENDATION
--------------
${selectedReport.recommendation || "N/A"}
    `.trim();

    try {
      await navigator.clipboard.writeText(reportText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error("Failed to copy report to clipboard:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = reportText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  if (!student) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle className="student-report-modal-title">
        <Box display="flex" alignItems="center">
          {/* Left side: Back button and title */}
          <Box display="flex" alignItems="center" gap={2} flexGrow={1}>
            {showReportDetails ? (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                className="report-modal-back-button"
              >
                <Button onClick={handleBackToList} size="small">
                  <ArrowBackIcon color="white" />
                </Button>
                <Typography variant="h6">
                  Student Comprehensive Report
                </Typography>
              </Box>
            ) : (
              <Typography variant="h6">
                Student Comprehensive Reports
              </Typography>
            )}
          </Box>
          {/* Right side: Close button */}
          <Box>
            <Button
              onClick={handleClose}
              className="student-report-modal-close-button"
            >
              <CloseIcon />
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent className="student-report-modal-content">
        {!showReportDetails ? (
          // Reports List View
          <Box>
            {/* Redesigned Student Info Section */}
            <Box
              className="student-info-section"
              mb={3}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              {/* Avatar or Icon */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: 28,
                    fontWeight: 700,
                    mr: 2,
                  }}
                >
                  {(student.name || student.username || "N/A")
                    .charAt(0)
                    .toUpperCase()}
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {student.name || student.username || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Student ID:{" "}
                    <span style={{ fontFamily: "monospace", fontWeight: 500 }}>
                      {student?.id}
                    </span>
                  </Typography>
                </Box>
              </Box>
              {/* Info Stats */}
              <Box
                sx={{
                  display: "flex",
                  gap: 4,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="caption" color="textSecondary">
                    Total Reports
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary.main"
                  >
                    {reports.length}
                  </Typography>
                </Box>
                {/* You can add more stats here if needed */}
              </Box>
            </Box>

            {/* Generate New Report Button */}
            <Box mb={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateNewReport}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
                fullWidth
              >
                {loading ? "Generating Report..." : "Generate New Report"}
              </Button>
            </Box>

            {/* Reports Table */}
            {reports.length > 0 ? (
              <TableContainer component={Paper} elevation={1}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Generated Date</TableCell>
                      <TableCell>Total Sessions</TableCell>
                      <TableCell>Total Quizzes</TableCell>
                      <TableCell>Overall Performance</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.map((report) => (
                      <TableRow key={report?.id}>
                        <TableCell>
                          {new Date(report?.created_at)?.toLocaleDateString()}
                        </TableCell>
                        <TableCell>{report?.total_sessions}</TableCell>
                        <TableCell>{report?.total_quizzes}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={
                              report?.overall_performance > 75
                                ? "success.main"
                                : report?.overall_performance > 50
                                ? "warning.main"
                                : "error.main"
                            }
                            fontWeight={500}
                          >
                            {report?.overall_performance}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="View Report Details" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleViewReport(report)}
                                color="primary"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            {onDeleteReport && (
                              <Tooltip title="Delete Report" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => onDeleteReport(report)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="textSecondary">
                  No comprehensive reports generated yet.
                </Typography>
                <Typography variant="body2" color="textSecondary" mt={1}>
                  Click "Generate New Comprehensive Report" to create the first
                  report.
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          // Report Details View
          <Box>
            {selectedReport && (
              <>
                {/* Report Summary */}
                <Box className="report-summary-section" mb={3}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="h6">Report Summary</Typography>
                    <Tooltip
                      title={
                        copySuccess ? "Copied!" : "Copy Report to Clipboard"
                      }
                      arrow
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyReport}
                        color={copySuccess ? "success" : "primary"}
                        sx={{
                          minWidth: "auto",
                          px: 2,
                          ...(copySuccess && {
                            backgroundColor: "success.light",
                            color: "success.contrastText",
                            "&:hover": {
                              backgroundColor: "success.main",
                            },
                          }),
                        }}
                      >
                        {copySuccess ? "Copied!" : "Copy"}
                      </Button>
                    </Tooltip>
                  </Box>
                  <Box display="flex" gap={3} flexWrap="wrap">
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Generated Date
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {new Date(
                          selectedReport.created_at
                        ).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total Sessions
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedReport?.total_sessions}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Total Quizzes
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedReport?.total_quizzes}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Overall Performance
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        color={
                          selectedReport.overall_performance > 75
                            ? "success.main"
                            : selectedReport.overall_performance > 50
                            ? "warning.main"
                            : "error.main"
                        }
                      >
                        {selectedReport.overall_performance}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Report Content */}
                <Box className="report-content-section">
                  {/* Strength Points */}
                  <Box className="report-section" mb={3}>
                    <Typography
                      variant="subtitle1"
                      className="report-label"
                      gutterBottom
                    >
                      Strength Points:
                    </Typography>
                    <Typography variant="body1" className="report-text">
                      {selectedReport.strength_points || "N/A"}
                    </Typography>
                  </Box>

                  {/* Points to Improve */}
                  <Box className="report-section" mb={3}>
                    <Typography
                      variant="subtitle1"
                      className="report-label"
                      gutterBottom
                    >
                      Points to Improve:
                    </Typography>
                    <Typography variant="body1" className="report-text">
                      {selectedReport.points_to_improve || "N/A"}
                    </Typography>
                  </Box>

                  {/* Recommendation */}
                  <Box className="report-section">
                    <Typography
                      variant="subtitle1"
                      className="report-label"
                      gutterBottom
                    >
                      Recommendation:
                    </Typography>
                    <Typography variant="body1" className="report-text">
                      {selectedReport.recommendation || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      {/* <DialogActions>
        <Button onClick={handleClose} className="student-report-modal-close-button">
          Close
        </Button>
      </DialogActions> */}
    </Dialog>
  );
};

export default StudentComprehensiveReportModal;
